/**
 * `journal.json`: the self-report a person gives between the recording and
 * processing, in the shape `calibrations/backend/app.py::api_journal` writes
 * and `brainspot-backend` reads as a sidecar (`schema_version` 1).
 *
 * The vocabulary is the web app's own `lib/selfReport.ts`, which copied it from
 * the acquisition app — one set of words for one question. A skipped journal
 * produces NO payload (`null`): the backend treats an absent file as "say
 * nothing", and an empty file would turn a skip into a claim.
 */

import {
  DRAFT_FIELDS,
  EMPTY_DRAFT,
  JOURNAL_SCHEMA_VERSION,
  NUMERIC_FIELDS,
  STATE_PAIRS,
  readClockTime,
  readNumeric,
  type JournalCaffeineLast,
  type JournalIntensity,
  type JournalQuality,
  type JournalState,
  type JournalTag,
  type JournalWhenAgo,
  type NumericField,
  type SelfReportDraft,
} from '../selfReport'

export interface JournalPayload {
  schema_version: typeof JOURNAL_SCHEMA_VERSION
  answered_at: string
  state: JournalState
  state_quadrant: (typeof STATE_PAIRS)[JournalState]
  sleep_hours: number | null
  sleep_quality: JournalQuality | null
  woke_at_local: string | null
  exercised: boolean | null
  exercise_finished: JournalWhenAgo | null
  exercise_intensity: JournalIntensity | null
  exercise_minutes: number | null
  caffeine_cups: number | null
  caffeine_last: JournalCaffeineLast | null
  alcohol_drinks: number | null
  water_glasses: number | null
  stress_1_10: number | null
  tags: JournalTag[]
  notes: string
}

/**
 * A draft with nothing in it is a skip, not an answer.
 *
 * ── OVER `DRAFT_FIELDS`, NOT A CHAIN OF `&&` ────────────────────────────────
 *
 * This was fifteen hand-written clauses. The failure mode is quiet and total: a
 * field missing from the chain is a field that cannot keep a journal alive, so a
 * person who answered only "Woke up at 06:40" has it read as a skip and **no
 * `journal.json` is written at all**. Not a wrong value — no document, at the one
 * moment the answer cannot be re-entered, with the UI reporting success.
 *
 * Driving it off `DRAFT_FIELDS` and `EMPTY_DRAFT` means the list cannot fall
 * behind the shape: a new field is unanswered when it still equals its own empty
 * value, which is what "unanswered" means everywhere else in this module.
 *
 * That sentence was a belief until `DRAFT_FIELDS` was given a type that enforces
 * it. `readonly DraftField[]` only said every entry IS a field; a field added to
 * `SelfReportDraft` and forgotten in the list compiled clean and came straight
 * back here as a dropped answer. `CompleteDraftFields` (`lib/selfReport.ts`) is
 * now the list's type and fails to compile naming the missing key, so the claim
 * above is checked rather than asserted.
 *
 * Two fields are not `===` comparisons and both are deliberate. `tags` is an
 * array, so identity would say "answered" for every draft. `notes` is trimmed,
 * because whitespace is not an answer — but ONLY for this decision: a note that
 * has words is sent exactly as typed (`buildPatch`), since trimming would rewrite
 * the one free-text field a person wrote in their own words.
 */
export function draftIsEmpty(draft: SelfReportDraft): boolean {
  return DRAFT_FIELDS.every((field) => {
    if (field === 'tags') return draft.tags.length === 0
    if (field === 'notes') return draft.notes.trim() === ''
    return draft[field] === EMPTY_DRAFT[field]
  })
}

function numberOrNull(field: NumericField, text: string): number | null {
  const read = readNumeric(text, NUMERIC_FIELDS[field])
  return read.kind === 'value' ? read.value : null
}

/**
 * The same shape for the clock field: a value, or nothing.
 *
 * An unreadable draft becomes `null` rather than travelling as text. The journal
 * goes INSIDE the recording's archive and is read back by the backend's
 * contract, where `woke_at_local` is pattern-checked — so a bad value here would
 * not be refused at a form, it would ride along in the tar and arrive as a
 * schema-2 journal the contract has to drop a cell from. The form blocks the
 * save; this is what the save would send if it did not.
 */
function clockOrNull(text: string): string | null {
  const read = readClockTime(text)
  return read.kind === 'value' ? read.value : null
}

/**
 * The payload, or `null` when the draft says nothing. `state` is required by
 * the writer's contract; a draft with answers but no felt state is reported
 * as `neutral` so the other answers are not thrown away.
 */
export function journalPayload(draft: SelfReportDraft, answeredAt: Date = new Date()): JournalPayload | null {
  if (draftIsEmpty(draft)) return null
  const state: JournalState = draft.state ?? 'neutral'
  return {
    schema_version: JOURNAL_SCHEMA_VERSION,
    answered_at: answeredAt.toISOString(),
    state,
    state_quadrant: STATE_PAIRS[state],
    sleep_hours: numberOrNull('sleep_hours', draft.sleep_hours),
    sleep_quality: draft.sleep_quality,
    woke_at_local: clockOrNull(draft.woke_at_local),
    exercised: draft.exercised,
    exercise_finished: draft.exercise_finished,
    exercise_intensity: draft.exercise_intensity,
    exercise_minutes: numberOrNull('exercise_minutes', draft.exercise_minutes),
    caffeine_cups: numberOrNull('caffeine_cups', draft.caffeine_cups),
    caffeine_last: draft.caffeine_last,
    alcohol_drinks: numberOrNull('alcohol_drinks', draft.alcohol_drinks),
    water_glasses: numberOrNull('water_glasses', draft.water_glasses),
    stress_1_10: draft.stress_1_10,
    tags: [...draft.tags],
    notes: draft.notes,
  }
}
